import React, { useState, useEffect, useMemo, useCallback } from 'react';
import BrokerCsvUploadModal from './BrokerCsvUploadModal';
import DefaultCsvUploadModal from './DefaultCsvUploadModal';
import {
  ManualTransactionFormData as TransactionFormData,
  getDataQualityReviewMessage,
  isNeedsReviewTransaction,
} from './ManualTransactionForm'; // Use the updated interface name
import ConfirmationModal from '../../../../shared/components/ui/ConfirmationModal';
import { useAccountTransactions } from '../../hooks/useAccountTransactions';

// Define the expected account structure directly here or import if defined globally
interface Account {
  id: string;
  accountName: string;
  transactions?: TransactionFormData[]; // Optional - transactions are now fetched via useAccountTransactions
  isHidden?: boolean;
  // Add other necessary fields from InvestmentAccount if used, like logoUrl, accountType
  logoUrl: string | null;
  company: string; // Changed from accountType
  createdAt: string;
  current_value?: number | null; // Add current value field
  current_value_updated_at?: string | null; // <<-- Add timestamp field
  brokerageAccountId?: string | null;
  isSnapTradeLinked?: boolean;
}

interface EditAccountTransactionsProps {
  account: Account; // Changed to accept the full account object
  // Removed redundant props: accountId, accountName, transactions, isHidden
  onBack: () => void;
  onUpdateAccountName: (newName: string) => Promise<void>; // Callback to update name in DashboardMain
  existingAccountNames: string[]; // For validation
  onAddTransactionClick: () => void; // Add prop for Add button
  // Add props for transaction actions
  onToggleTransactionHidden: (transactionId: string) => Promise<void>;
  onEditTransactionClick: (transaction: TransactionFormData) => void; // Use updated type
  onDeleteTransaction: (transactionId: string) => Promise<void>;
  onUpdateAccountHidden: (isHidden: boolean) => void; // Add handler for account hide
  onUpdateAccountValue: (newValue: number) => Promise<void>; // <<-- New Prop
  onLinkAccountClick: () => void;
  onUnlinkAccount: () => Promise<void>;
  onRefreshImpacted2?: (accountId: string, hints: { tickers: string[]; dividendTickers: string[]; cash_changed: boolean }) => Promise<void> | void;
  initialCsvSuccessMessage?: string | null;
  initialTransactionSuccessMessage?: string | null;
  initialLinkSuccessMessage?: string | null;
}

type TransactionFilterState = {
  searchText: string;
  showNeedsReviewOnly: boolean;
};

const getTransactionFilterStorageKey = (accountId: string) =>
  `vester:portfolio:transactionFilters:${accountId}`;

const readTransactionFilterState = (accountId: string): TransactionFilterState => {
  if (typeof window === 'undefined') {
    return { searchText: '', showNeedsReviewOnly: false };
  }

  try {
    const saved = sessionStorage.getItem(getTransactionFilterStorageKey(accountId));
    if (!saved) {
      return { searchText: '', showNeedsReviewOnly: false };
    }
    const parsed = JSON.parse(saved) as Partial<TransactionFilterState>;
    return {
      searchText: typeof parsed.searchText === 'string' ? parsed.searchText : '',
      showNeedsReviewOnly: parsed.showNeedsReviewOnly === true,
    };
  } catch {
    return { searchText: '', showNeedsReviewOnly: false };
  }
};

export const clearAccountTransactionFilters = (accountId: string) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(getTransactionFilterStorageKey(accountId));
  } catch {}
};

const EditAccountTransactions: React.FC<EditAccountTransactionsProps> = ({
  account, // Destructure the account object
  onBack,
  onUpdateAccountName,
  existingAccountNames,
  onAddTransactionClick,
  onToggleTransactionHidden,
  onEditTransactionClick,
  onDeleteTransaction,
  onUpdateAccountHidden, // Destructure account hide handler
  onUpdateAccountValue, // <<-- New Prop
  onLinkAccountClick,
  onUnlinkAccount,
  onRefreshImpacted2,
  initialCsvSuccessMessage,
  initialTransactionSuccessMessage,
  initialLinkSuccessMessage,
}) => {

  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState(account.accountName); // Use account.accountName
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSavingName, setIsSavingName] = useState(false);

  // Value Editing State <<-- New State
  const [isEditingValue, setIsEditingValue] = useState(false);
  const [newValue, setNewValue] = useState<string>(account.current_value?.toString() ?? ''); // Store as string for input
  const [valueError, setValueError] = useState<string | null>(null);
  const [isSavingValue, setIsSavingValue] = useState(false);

  // Hide/unhide account confirmation modal state
  const [isAccountHideConfirmationOpen, setIsAccountHideConfirmationOpen] = useState(false);
  const [isAccountUnhideConfirmationOpen, setIsAccountUnhideConfirmationOpen] = useState(false);
  const [isHidingAccount, setIsHidingAccount] = useState(false);
  const [isUnlinkConfirmationOpen, setIsUnlinkConfirmationOpen] = useState(false);
  const [isUnlinkingAccount, setIsUnlinkingAccount] = useState(false);

  // Hide/unhide transaction confirmation modal state
  const [isHideConfirmationOpen, setIsHideConfirmationOpen] = useState(false);
  const [isUnhideConfirmationOpen, setIsUnhideConfirmationOpen] = useState(false);
  const [transactionToHide, setTransactionToHide] = useState<{ id: string; ticker: string | null; type: string } | null>(null);
  const [isHidingTransaction, setIsHidingTransaction] = useState(false);

  // Delete transaction confirmation modal state
  const [isDeleteTransactionConfirmationOpen, setIsDeleteTransactionConfirmationOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<{ id: string; accountId: string } | null>(null);
  const [isDeletingTransaction, setIsDeletingTransaction] = useState(false);

  // CSV Upload modal state - initialize from sessionStorage
  const [isCsvOpen, setIsCsvOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const storageKey = `vester:portfolio:csvModal:etrade:${account.id}`;
    const saved = sessionStorage.getItem(storageKey);
    return saved === 'true';
  });
  
  const [isDefaultCsvOpen, setIsDefaultCsvOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const storageKey = `vester:portfolio:csvModal:default:${account.id}`;
    const saved = sessionStorage.getItem(storageKey);
    return saved === 'true';
  });

  const [isSchwabCsvOpen, setIsSchwabCsvOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const storageKey = `vester:portfolio:csvModal:schwab:${account.id}`;
    const saved = sessionStorage.getItem(storageKey);
    return saved === 'true';
  });

  const [isFidelityCsvOpen, setIsFidelityCsvOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const storageKey = `vester:portfolio:csvModal:fidelity:${account.id}`;
    const saved = sessionStorage.getItem(storageKey);
    return saved === 'true';
  });

  const [isCoinbaseCsvOpen, setIsCoinbaseCsvOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const storageKey = `vester:portfolio:csvModal:coinbase:${account.id}`;
    const saved = sessionStorage.getItem(storageKey);
    return saved === 'true';
  });

  const [isVanguardCsvOpen, setIsVanguardCsvOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const storageKey = `vester:portfolio:csvModal:vanguard:${account.id}`;
    const saved = sessionStorage.getItem(storageKey);
    return saved === 'true';
  });

  const [csvSuccessMessage, setCsvSuccessMessage] = useState<string | null>(initialCsvSuccessMessage ?? null);
  const [csvWarningMessage, setCsvWarningMessage] = useState<string | null>(null);
  const [transactionSuccessMessage, setTransactionSuccessMessage] = useState<string | null>(initialTransactionSuccessMessage ?? null);
  const [linkSuccessMessage, setLinkSuccessMessage] = useState<string | null>(initialLinkSuccessMessage ?? null);
  const [transactionSearchText, setTransactionSearchText] = useState(
    () => readTransactionFilterState(account.id).searchText
  );
  const [showNeedsReviewOnly, setShowNeedsReviewOnly] = useState(
    () => readTransactionFilterState(account.id).showNeedsReviewOnly
  );

  // Persist CSV modal states to sessionStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const etradeKey = `vester:portfolio:csvModal:etrade:${account.id}`;
    const schwabKey = `vester:portfolio:csvModal:schwab:${account.id}`;
    const fidelityKey = `vester:portfolio:csvModal:fidelity:${account.id}`;
    const coinbaseKey = `vester:portfolio:csvModal:coinbase:${account.id}`;
    const vanguardKey = `vester:portfolio:csvModal:vanguard:${account.id}`;
    const defaultKey = `vester:portfolio:csvModal:default:${account.id}`;

    if (isCsvOpen) {
      sessionStorage.setItem(etradeKey, 'true');
    } else {
      sessionStorage.removeItem(etradeKey);
    }

    if (isSchwabCsvOpen) {
      sessionStorage.setItem(schwabKey, 'true');
    } else {
      sessionStorage.removeItem(schwabKey);
    }

    if (isFidelityCsvOpen) {
      sessionStorage.setItem(fidelityKey, 'true');
    } else {
      sessionStorage.removeItem(fidelityKey);
    }

    if (isCoinbaseCsvOpen) {
      sessionStorage.setItem(coinbaseKey, 'true');
    } else {
      sessionStorage.removeItem(coinbaseKey);
    }

    if (isVanguardCsvOpen) {
      sessionStorage.setItem(vanguardKey, 'true');
    } else {
      sessionStorage.removeItem(vanguardKey);
    }

    if (isDefaultCsvOpen) {
      sessionStorage.setItem(defaultKey, 'true');
    } else {
      sessionStorage.removeItem(defaultKey);
    }
  }, [isCsvOpen, isSchwabCsvOpen, isFidelityCsvOpen, isCoinbaseCsvOpen, isVanguardCsvOpen, isDefaultCsvOpen, account.id]);

  useEffect(() => {
    const savedFilters = readTransactionFilterState(account.id);
    setTransactionSearchText(savedFilters.searchText);
    setShowNeedsReviewOnly(savedFilters.showNeedsReviewOnly);
  }, [account.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(
        getTransactionFilterStorageKey(account.id),
        JSON.stringify({
          searchText: transactionSearchText,
          showNeedsReviewOnly,
        })
      );
    } catch {}
  }, [account.id, transactionSearchText, showNeedsReviewOnly]);

  // Auto-dismiss CSV success banner after ~6s (same as warning message)
  useEffect(() => {
    if (csvSuccessMessage) {
      const t = setTimeout(() => setCsvSuccessMessage(null), 6000);
      return () => clearTimeout(t);
    }
  }, [csvSuccessMessage]);

  // Auto-dismiss transaction success message after 10s (longer for new ticker message)
  useEffect(() => {
    if (transactionSuccessMessage) {
      const t = setTimeout(() => setTransactionSuccessMessage(null), 10000);
      return () => clearTimeout(t);
    }
  }, [transactionSuccessMessage]);

  useEffect(() => {
    if (linkSuccessMessage) {
      const t = setTimeout(() => setLinkSuccessMessage(null), 3000);
      return () => clearTimeout(t);
    }
  }, [linkSuccessMessage]);
  
  // Auto-dismiss CSV warning banner after ~6s (longer than success message)
  useEffect(() => {
    if (csvWarningMessage) {
      const t = setTimeout(() => setCsvWarningMessage(null), 6000);
      return () => clearTimeout(t);
    }
  }, [csvWarningMessage]);
  
  useEffect(() => {
    const openHandler = (e: any) => {
      setIsCsvOpen(true);
    };
    window.addEventListener('open-etrade-csv-upload', openHandler as any);
    return () => window.removeEventListener('open-etrade-csv-upload', openHandler as any);
  }, []);

  // Refresh helper to be passed to the modal (v2 invalidation-driven)
  const handleRefreshImpacted2 = async (
    acctId: string,
    hints: { tickers: string[]; dividendTickers: string[]; cash_changed: boolean }
  ) => {
    if (onRefreshImpacted2) {
      try {
        await onRefreshImpacted2(acctId, hints);
      } catch (e) {
        console.error('onRefreshImpacted2 failed', e);
      }
    } else {
      console.warn('onRefreshImpacted2 not provided by parent; skipping UI refresh');
    }
  };

  // Fetch transactions using React Query hook
  const transactionsQuery = useAccountTransactions(account.id);
  
  // Sort transactions by date, most recent first
  const sortedTransactions = useMemo(() => {
    const transactions = transactionsQuery.data || [];
    return [...transactions].sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [transactionsQuery.data]);

  // --- Name Editing Handlers ---
  const handleEditNameClick = () => {
    setNewName(account.accountName); // Reset input to current name
    setNameError(null);
    setIsEditingName(true);
    setIsEditingValue(false); // Ensure value edit is closed
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setNameError(null);
  };

  const handleSaveName = async () => {
    const trimmedName = newName.trim();
    if (trimmedName === account.accountName) { // Compare with account.accountName
      setIsEditingName(false); // No change, just close edit mode
      return;
    }
    if (!trimmedName) {
        setNameError("Account name cannot be empty.");
        return;
    }
    // Check if name already exists (excluding the current account name)
    if (existingAccountNames.filter(name => name !== account.accountName).includes(trimmedName)) { // Exclude current name
        setNameError(`You already have an account with the name "${trimmedName}".`);
        return;
    }
    
    setIsSavingName(true);
    setNameError(null);
    try {
        await onUpdateAccountName(trimmedName); // Call the prop to update state in parent
        setIsEditingName(false);
    } catch (error) { 
        console.error("Error updating account name:", error);
        setNameError("Failed to update name. Please try again.");
    } finally {
        setIsSavingName(false);
    }
  };
  // --- End Name Editing Handlers ---

  const isLinkedToSnapTrade = account.isSnapTradeLinked || Boolean(account.brokerageAccountId);

  const handleLinkStatusClick = () => {
    if (isLinkedToSnapTrade) {
      setIsUnlinkConfirmationOpen(true);
      return;
    }
    onLinkAccountClick();
  };

  const handleCancelUnlinkAccount = () => {
    if (isUnlinkingAccount) return;
    setIsUnlinkConfirmationOpen(false);
  };

  const handleConfirmUnlinkAccount = async () => {
    if (isUnlinkingAccount) return;
    setIsUnlinkingAccount(true);
    try {
      await onUnlinkAccount();
      setIsUnlinkConfirmationOpen(false);
    } catch (error) {
      console.error('Error unlinking account:', error);
    } finally {
      setIsUnlinkingAccount(false);
    }
  };

  // --- Value Editing Handlers <<-- New Handlers ---
  const handleEditValueClick = () => {
    setNewValue(account.current_value?.toString() ?? ''); // Set current value (as string)
    setValueError(null);
    setIsEditingValue(true);
    setIsEditingName(false); // Ensure name edit is closed
  };

  const handleCancelEditValue = () => {
    setIsEditingValue(false);
    setValueError(null);
  };

  const handleSaveValue = async () => {
      const numericValue = parseFloat(newValue);
      if (isNaN(numericValue)) {
          setValueError("Please enter a valid number for the account value.");
          return;
      }
      // Optional: Check if value actually changed
      // if (numericValue === account.current_value) {
      //     setIsEditingValue(false);
      //     return;
      // }

      setIsSavingValue(true);
      setValueError(null);
      try {
          await onUpdateAccountValue(numericValue);
          setIsEditingValue(false);
      } catch (error) {
          console.error("Error updating account value:", error);
          setValueError("Failed to update value. Please try again.");
      } finally {
          setIsSavingValue(false);
      }
  };

  // --- Account Hide/Unhide Handlers ---
  // Handle account hide confirmation modal confirm
  const handleConfirmHideAccount = async () => {
    if (isHidingAccount) return;
    
    setIsHidingAccount(true);
    try {
      await onUpdateAccountHidden(true);
      // Close modals after successful operation
      setIsAccountHideConfirmationOpen(false);
    } catch (error) {
      // Keep modal open on error
      console.error('Error hiding/unhiding account:', error);
    } finally {
      setIsHidingAccount(false);
    }
  };

  // Handle account hide confirmation modal cancel
  const handleCancelHideAccount = () => {
    if (isHidingAccount) return; // Prevent closing during loading
    setIsAccountHideConfirmationOpen(false);
  };

  // Handle account unhide confirmation modal confirm
  const handleConfirmUnhideAccount = async () => {
    if (isHidingAccount) return;
    
    setIsHidingAccount(true);
    try {
      await onUpdateAccountHidden(false);
      // Close modals after successful operation
      setIsAccountUnhideConfirmationOpen(false);
    } catch (error) {
      // Keep modal open on error
      console.error('Error hiding/unhiding account:', error);
    } finally {
      setIsHidingAccount(false);
    }
  };

  // Handle account unhide confirmation modal cancel
  const handleCancelUnhideAccount = () => {
    if (isHidingAccount) return; // Prevent closing during loading
    setIsAccountUnhideConfirmationOpen(false);
  };

  // --- Transaction Hide/Unhide Handlers ---
  // Helper to get confirmation message based on transaction type
  const getConfirmationMessage = (
    transaction: { ticker: string | null; type: string },
    action: 'hide' | 'unhide'
  ): React.ReactNode => {
    const equityOptionTypes = ['Buy', 'Sell', 'Short Sell', 'Buy to Cover', 'Call', 'Call Sell', 'Put', 'Put Sell'];
    const actionText = action === 'hide' ? 'hide' : 'unhide';
    
    if (equityOptionTypes.includes(transaction.type)) {
      // Equity/Option transactions
      return transaction.ticker 
        ? <>Are you sure you want to {actionText} all instances of this ticker (<strong>{transaction.ticker}</strong>) in <em>this</em> account for all your friends?<br />(Note: Only Gold tier friends are affected)</>
        : <>Are you sure you want to {actionText} all instances of this ticker in <em>this</em> account for all your friends?<br />(Note: Only Gold tier friends are affected)</>;
    } else if (transaction.type === 'Interest') {
      // Interest: hide/unhide just this one transaction
      return <>Are you sure you want to {actionText} <strong>just this interest transaction</strong> from all your friends?<br />(Note: Only Gold tier friends are affected)</>;
    } else if (transaction.type === 'Dividend' || transaction.type === 'Dividend in Lieu') {
      // Dividend transactions - hide all instances of the ticker
      return transaction.ticker 
        ? <>Are you sure you want to {actionText} all instances of this ticker (<strong>{transaction.ticker}</strong>) in <em>this</em> account for all your friends?<br />(Note: Only Gold tier friends are affected)</>
        : <>Are you sure you want to {actionText} all instances of this ticker in <em>this</em> account for all your friends?<br />(Note: Only Gold tier friends are affected)</>;
    } else {
      // Cash transactions (Adjustment, Deposit, Withdrawal, etc.)
      return <>Are you sure you want to {actionText} this <strong>specific cash transaction</strong> from all your friends?<br />(Note: Not even Gold tier friends see this data, but it may affect your total % gain calculations seen by friends)</>;
    }
  };

  // Handle transaction hide button click
  const handleHideTransactionClick = (transaction: TransactionFormData) => {
    if (!transaction.id) return;
    
    setTransactionToHide({
      id: transaction.id,
      ticker: transaction.ticker || null,
      type: transaction.type || '',
    });
    
    // Show confirmation modal for both hiding and unhiding
    if (!transaction.isHidden) {
      // Hiding - show hide confirmation
      setIsHideConfirmationOpen(true);
    } else {
      // Unhiding - show unhide confirmation
      setIsUnhideConfirmationOpen(true);
    }
  };

  // Handle confirmation modal confirm (works for both hide and unhide)
  const handleConfirmTransaction = async () => {
    if (!transactionToHide || isHidingTransaction) return;
    
    setIsHidingTransaction(true);
    try {
      await onToggleTransactionHidden(transactionToHide.id);
      // Close modals after successful operation
      setIsHideConfirmationOpen(false);
      setIsUnhideConfirmationOpen(false);
      setTransactionToHide(null);
    } catch (error) {
      // Keep modal open on error
      console.error('Error hiding/unhiding transaction:', error);
    } finally {
      setIsHidingTransaction(false);
    }
  };

  // Handle hide confirmation modal cancel
  const handleCancelHideTransaction = () => {
    if (isHidingTransaction) return; // Prevent closing during loading
    setTransactionToHide(null);
    setIsHideConfirmationOpen(false);
  };

  // Handle unhide confirmation modal cancel
  const handleCancelUnhideTransaction = () => {
    if (isHidingTransaction) return; // Prevent closing during loading
    setTransactionToHide(null);
    setIsUnhideConfirmationOpen(false);
  };

  // Handle delete button click
  const handleDeleteTransactionClick = (transactionId: string) => {
    if (!transactionId || !account.id) return;
    setTransactionToDelete({ id: transactionId, accountId: account.id });
    setIsDeleteTransactionConfirmationOpen(true);
  };

  // Handle delete confirmation
  const handleConfirmDeleteTransaction = async () => {
    if (!transactionToDelete || isDeletingTransaction) return;
    setIsDeletingTransaction(true);
    try {
      await onDeleteTransaction(transactionToDelete.id);
      setTransactionToDelete(null);
      setIsDeleteTransactionConfirmationOpen(false);
    } catch (error) {
      // Keep modal open on error (toast already shown by hook)
    } finally {
      setIsDeletingTransaction(false);
    }
  };

  // Handle delete cancel
  const handleCancelDeleteTransaction = () => {
    if (isDeletingTransaction) return;
    setTransactionToDelete(null);
    setIsDeleteTransactionConfirmationOpen(false);
  };

  const formatCurrency = useCallback((value: number | undefined | null) => {
    if (value === undefined || value === null) return ''; 
    const options: Intl.NumberFormatOptions = { // Explicitly type options
        style: 'currency', // Use the literal type
        currency: 'USD', 
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    };
    const numberFormat = new Intl.NumberFormat('en-US', options);
    return numberFormat.format(value);
  }, []);

  // Helper to format date/time string
  const formatDate = useCallback((dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
        // Assuming dateString is ISO format
        return new Date(dateString).toLocaleString(undefined, {
            year: 'numeric', month: 'numeric', day: 'numeric', 
            hour: 'numeric', minute: '2-digit' 
        });
    } catch (e) {
        console.error("Error parsing date:", dateString, e);
        return 'Invalid Date';
    }
  }, []);

  // Helper to format expiration date (YYYY-MM-DD)
  const formatExpirationDate = useCallback((dateString: string | null | undefined): string => {
     if (!dateString) return '';
     try {
         // Input might be YYYY-MM-DD already
         const date = new Date(dateString + 'T00:00:00'); // Add time part to avoid timezone issues
         return date.toLocaleDateString(undefined, {
             year: 'numeric', month: 'numeric', day: 'numeric'
         });
     } catch (e) {
         console.error("Error parsing expiration date:", dateString, e);
         return 'Invalid Date';
     }
   }, []);

  const formatTransactionDetails = useCallback((tx: TransactionFormData): string => {
    if (['Buy', 'Sell', 'Short Sell', 'Buy to Cover'].includes(tx.type)) {
      return `${tx.quantity?.toLocaleString() || '?'} ${tx.ticker || '?'} @ ${formatCurrency(tx.price_per_unit)}`;
    }
    if (['Call', 'Put', 'Call Sell', 'Put Sell'].includes(tx.type)) {
      const qtyText = (tx.contracts != null ? Math.abs(Number(tx.contracts)).toLocaleString() : '?');
      return `${qtyText} ${tx.ticker || '?'} ${formatCurrency(tx.strike_price)} ${tx.type.startsWith('Call') ? 'Call' : 'Put'} Exp ${formatExpirationDate(tx.expiration_date)}`;
    }
    if (tx.type === 'Dividend') {
      return `Dividend for ${tx.ticker || 'Unknown'}`;
    }
    if (tx.type === 'Dividend in Lieu') {
      return `Dividend in Lieu for ${tx.ticker || 'Unknown'}`;
    }
    if (tx.type === 'Interest') {
      return 'Interest Received';
    }
    if (tx.type === 'Adjustment') {
      return 'Adjustment';
    }
    if (tx.type === 'Deposit') {
      return `$${Math.abs(Number(tx.total_amount ?? 0)).toFixed(2)} Deposit`;
    }
    if (tx.type === 'Withdrawal') {
      return `$${Math.abs(Number(tx.total_amount ?? 0)).toFixed(2)} Withdrawal`;
    }
    return '';
  }, [formatCurrency, formatExpirationDate]);

  const formatUnknownBrokerSummary = useCallback((tx: TransactionFormData): string => {
    const brokerQuantity = Number(tx.brokerage_quantity ?? 0);
    const totalAmount = Number(tx.total_amount ?? 0);
    const displayAmount = totalAmount !== 0 || brokerQuantity === 0
      ? totalAmount
      : brokerQuantity;
    return `(Ticker: ${tx.ticker || 'NULL'}, Amount: ${formatCurrency(displayAmount)}, Type: ${tx.brokerage_type || 'UNKNOWN'})`;
  }, [formatCurrency]);

  const formatTransactionNotes = useCallback((tx: TransactionFormData): string => {
    const notes = tx.notes || '';
    if (tx.type !== 'UNKNOWN') {
      return notes;
    }
    const summary = formatUnknownBrokerSummary(tx);
    if (notes.startsWith('Needs Review: ')) {
      return notes.replace('Needs Review: ', `Needs Review: ${summary} `);
    }
    return notes ? `${summary} ${notes}` : summary;
  }, [formatUnknownBrokerSummary]);

  const filteredTransactions = useMemo(() => {
    const query = transactionSearchText.trim().toLowerCase();
    return sortedTransactions.filter((tx) => {
      if (showNeedsReviewOnly && !isNeedsReviewTransaction(tx)) {
        return false;
      }
      if (!query) {
        return true;
      }

      const details = formatTransactionDetails(tx);
      const notes = formatTransactionNotes(tx);
      const reviewMessage = isNeedsReviewTransaction(tx)
        ? getDataQualityReviewMessage(tx.data_quality_reason)
        : '';
      const searchableText = [
        tx.type,
        tx.ticker,
        details,
        notes,
        formatDate(tx.date),
        formatCurrency(typeof tx.total_amount === 'number' ? tx.total_amount : 0),
        tx.data_quality_reason,
        tx.brokerage_type,
        tx.brokerage_description,
        tx.brokerage_quantity,
        reviewMessage,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchableText.includes(query);
    });
  }, [formatCurrency, formatDate, formatTransactionDetails, formatTransactionNotes, showNeedsReviewOnly, sortedTransactions, transactionSearchText]);

  const hasActiveTransactionFilters = transactionSearchText.trim().length > 0 || showNeedsReviewOnly;

  // CSV Upload: broker gating (extensible for future brokers)
  const brokerKey = (account.company || '').toLowerCase();
  const supportsCsvUpload = brokerKey === 'etrade';
  const supportsSchwabCsvUpload = brokerKey === 'schwab';
  const supportsFidelityCsvUpload = brokerKey === 'fidelity';
  const supportsCoinbaseCsvUpload = brokerKey === 'coinbase';
  const supportsVanguardCsvUpload = brokerKey === 'vanguard';

  return (
    <div className="space-y-6">
      {/* CSV success banner (top of modal content, below modal title) */}
      {csvSuccessMessage && (
        <div className="p-3 rounded-md bg-green-50 text-green-800 border border-green-200">
          {csvSuccessMessage}
        </div>
      )}

      {linkSuccessMessage && (
        <div className="p-3 rounded-md bg-green-50 text-green-800 border border-green-200">
          {linkSuccessMessage}
        </div>
      )}
      
      {/* CSV warning banner */}
      {csvWarningMessage && (
        <div className="p-3 rounded-md bg-yellow-50 text-yellow-800 border border-yellow-200">
          {csvWarningMessage}
        </div>
      )}

      {/* --- Account Name & Value Section --- */} 
      <div className="p-3 bg-gray-50 rounded-md min-h-[64px] space-y-3"> {/* Add space-y */} 
         {/* Name Display/Edit Row */}
         <div className="flex items-center justify-between">
             {!isEditingName && !isEditingValue ? ( // Show name only if not editing name OR value
                <h2 className={`text-xl font-semibold mr-4 ${account.isHidden ? 'text-gray-400' : 'text-gray-800'}`}>{account.accountName}</h2>
             ) : isEditingName ? ( // Show name editing UI
                // --- Name Editing UI (copied from previous logic) --- 
                <div className="flex-grow space-y-2">
                    <div className="flex items-center space-x-2">
                        <input 
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            className={`flex-grow px-2 py-1 border rounded-md ${nameError ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-1 focus:ring-blue-500`}
                            aria-label="New account name"
                            disabled={isSavingName}
                        />
                        <button 
                            onClick={handleSaveName}
                            disabled={isSavingName || newName.trim() === ''}
                            className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 disabled:opacity-50"
                        >
                            {isSavingName ? 'Saving...' : 'Save'}
                        </button>
                        <button 
                            onClick={handleCancelEditName}
                            disabled={isSavingName}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                    {nameError && <p className="text-sm text-red-600">{nameError}</p>}
                </div>
             ) : ( // Otherwise (must be editing value), just show the static name
                <h2 className={`text-xl font-semibold mr-4 ${account.isHidden ? 'text-gray-400' : 'text-gray-800'}`}>{account.accountName}</h2>
             )}

            {/* Edit/Hide Buttons - Show only when NOT editing name or value */}
             {!isEditingName && !isEditingValue && (
                 <div className="flex items-center space-x-4">
                    <button
                      onClick={handleLinkStatusClick}
                      title={isLinkedToSnapTrade ? 'Unlink external account' : 'Link external account'}
                      className={`p-1 rounded hover:bg-green-100 focus:outline-none ${
                        isLinkedToSnapTrade ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      <span
                        className={`block h-5 w-5 ${isLinkedToSnapTrade ? 'bg-green-600' : 'bg-gray-400'}`}
                        style={{
                          WebkitMask: "url('/icon-link-lrg.png') center / contain no-repeat",
                          mask: "url('/icon-link-lrg.png') center / contain no-repeat",
                        }}
                        aria-hidden="true"
                      />
                      <span className="sr-only">
                        {isLinkedToSnapTrade ? 'Unlink external account' : 'Link external account'}
                      </span>
                    </button>
                    {/* Hide/Unhide Button */} 
                     <button
                         onClick={() => {
                           if (account.isHidden) {
                             setIsAccountUnhideConfirmationOpen(true);
                           } else {
                             setIsAccountHideConfirmationOpen(true);
                           }
                         }}
                         title={account.isHidden ? 'Unhide Account' : 'Hide Account'}
                         className="p-1 rounded text-gray-400 hover:bg-gray-200 hover:text-gray-600 focus:outline-none"
                     >
                         <img 
                           src={account.isHidden ? '/icon-unhide-lrg.png' : '/icon-hide-lrg.png'}
                           alt={account.isHidden ? 'Unhide Account' : 'Hide Account'}
                           className="h-5 w-5"
                         />
                     </button>
                     {/* Edit Name Button */} 
                     <button 
                         onClick={handleEditNameClick}
                         className="text-sm text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-100"
                         title="Edit Account Name"
                     >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                     </button>
                </div>
             )}
         </div>
         
         {/* Value Display/Edit Row */}
         <div className="flex items-center justify-between">
            {!isEditingValue ? ( // Show value and Edit button
                <>
                    <div className="flex flex-col items-start space-y-1 flex-grow">
                        <span className="text-sm text-gray-600">
                            Account value: {formatCurrency(account.current_value)} entered on {account.current_value_updated_at ? formatDate(account.current_value_updated_at) : 'Unknown date'}
                        </span>
                    </div>
                    <div className="flex-shrink-0">
                        {!isEditingName && (
                            <button
                                onClick={handleEditValueClick}
                                className="text-sm text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-100"
                                title="Update Account Value"
                            >
                                Update account value
                            </button>
                        )}
                    </div>
                </>
            ) : ( // Show value editing UI
                // --- Value Editing UI ---
                <div className="flex-grow space-y-2">
                    <div className="flex items-center space-x-2">
                        <input 
                            type="number" // Use number type
                            step="0.01" // Allow cents
                            placeholder="Current account value"
                            value={newValue}
                            onChange={(e) => setNewValue(e.target.value)}
                            className={`flex-grow px-2 py-1 border rounded-md ${valueError ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-1 focus:ring-blue-500`}
                            aria-label="New account value"
                            disabled={isSavingValue}
                        />
                        <button 
                            onClick={handleSaveValue}
                            disabled={isSavingValue}
                            className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 disabled:opacity-50"
                        >
                            {isSavingValue ? 'Saving...' : 'Save'}
                        </button>
                        <button 
                            onClick={handleCancelEditValue}
                            disabled={isSavingValue}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                    {valueError && <p className="text-sm text-red-600">{valueError}</p>}
                </div>
            )}
         </div>
      </div>
      {/* --- End Account Name & Value Section --- */} 
      
      {/* --- Add Transaction / Upload CSV Buttons --- */} 
       <div className="flex items-center space-x-3">
          {/* E*TRADE CSV Upload Button (conditional) */}
          {supportsCsvUpload && (
            <button
              onClick={() => setIsCsvOpen(true)}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 border border-dashed border-blue-600 text-blue-700 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              title="Upload E*TRADE CSV"
            >
              <img
                src="/icon-upload-lrg.png"
                alt="Upload"
                className="h-5 w-5"
                style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1234%) hue-rotate(200deg) brightness(95%) contrast(95%)' }}
              />
              <span>Upload E*TRADE CSV</span>
            </button>
          )}
          {/* Schwab CSV Upload Button (conditional) */}
          {supportsSchwabCsvUpload && (
            <button
              onClick={() => setIsSchwabCsvOpen(true)}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 border border-dashed border-blue-600 text-blue-700 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              title="Upload Schwab CSV"
            >
              <img
                src="/icon-upload-lrg.png"
                alt="Upload"
                className="h-5 w-5"
                style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1234%) hue-rotate(200deg) brightness(95%) contrast(95%)' }}
              />
              <span>Upload Schwab CSV</span>
            </button>
          )}
          {/* Fidelity CSV Upload Button (conditional) */}
          {supportsFidelityCsvUpload && (
            <button
              onClick={() => setIsFidelityCsvOpen(true)}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 border border-dashed border-blue-600 text-blue-700 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              title="Upload Fidelity CSV"
            >
              <img 
                src="/icon-upload-lrg.png" 
                alt="Upload" 
                className="h-5 w-5"
                style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1234%) hue-rotate(200deg) brightness(95%) contrast(95%)' }}
              />
              <span>Upload Fidelity CSV</span>
            </button>
          )}
          {/* Coinbase CSV Upload Button (conditional) */}
          {supportsCoinbaseCsvUpload && (
            <button
              onClick={() => setIsCoinbaseCsvOpen(true)}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 border border-dashed border-blue-600 text-blue-700 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              title="Upload Coinbase CSV"
            >
              <img 
                src="/icon-upload-lrg.png" 
                alt="Upload" 
                className="h-5 w-5"
                style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1234%) hue-rotate(200deg) brightness(95%) contrast(95%)' }}
              />
              <span>Upload Coinbase CSV</span>
            </button>
          )}
          {/* Vanguard CSV Upload Button (conditional) */}
          {supportsVanguardCsvUpload && (
            <button
              onClick={() => setIsVanguardCsvOpen(true)}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 border border-dashed border-blue-600 text-blue-700 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              title="Upload Vanguard CSV"
            >
              <img
                src="/icon-upload-lrg.png"
                alt="Upload"
                className="h-5 w-5"
                style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1234%) hue-rotate(200deg) brightness(95%) contrast(95%)' }}
              />
              <span>Upload Vanguard CSV</span>
            </button>
          )}
          {/* Default CSV Upload Button */}
          <button
            onClick={() => setIsDefaultCsvOpen(true)}
            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 border border-dashed border-gray-600 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            title="Upload Default CSV"
          >
            <img 
              src="/icon-upload-lrg.png" 
              alt="Upload" 
              className="h-5 w-5"
              style={{ filter: 'brightness(0) saturate(100%) invert(30%) sepia(8%) saturate(1352%) hue-rotate(177deg) brightness(94%) contrast(86%)' }}
            />
            <span>Upload Default CSV</span>
          </button>
          {/* Add Transaction Button */}
          <button 
             onClick={onAddTransactionClick}
             className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 border border-dashed border-green-600 text-green-700 rounded-lg hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
             </svg>
             <span>Add Transaction</span>
          </button>
       </div>
      {/* --- End Add Transaction / Upload CSV Buttons --- */} 
      {/* --- Transactions Section --- */} 
      <div>
        {/* Transaction Success Message */}
        {transactionSuccessMessage && (
          <div className="rounded-md bg-green-50 border border-green-200 p-4 mb-4">
            <p className="text-sm text-green-800">{transactionSuccessMessage}</p>
          </div>
        )}
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-medium text-gray-800">Transactions</h3>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label htmlFor="transaction-search" className="sr-only">
              Search transactions
            </label>
            <input
              id="transaction-search"
              type="text"
              value={transactionSearchText}
              onChange={(event) => setTransactionSearchText(event.target.value)}
              placeholder="Search transactions"
              className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
              <input
                type="checkbox"
                checked={showNeedsReviewOnly}
                onChange={(event) => setShowNeedsReviewOnly(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              Needs Review
            </label>
          </div>
        </div>
        
        {/* TODO: Add Transaction Button? Maybe keep it in the header */} 

        <div className="overflow-x-auto border border-gray-200 rounded-md shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>{/* Ensure no whitespace before first th */} 
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th> 
                <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                <th scope="col" className="sticky right-0 px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Actions</th> 
              </tr>{/* Ensure no whitespace after last th */} 
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactionsQuery.isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    Loading transactions...
                  </td>
                </tr>
              ) : transactionsQuery.isError ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-red-500">
                    Error loading transactions: {transactionsQuery.error instanceof Error ? transactionsQuery.error.message : 'Unknown error'}
                  </td>
                </tr>
              ) : sortedTransactions.length > 0 && filteredTransactions.length > 0 ? (
                filteredTransactions.map((tx) => {
                  const details = formatTransactionDetails(tx);
                  const notes = formatTransactionNotes(tx);
                  // Display amount is exactly what's stored in the DB
                  let displayAmount = typeof tx.total_amount === 'number' ? tx.total_amount : 0;

                  // Use the isHidden property from the transaction data
                  const txIsHidden = tx.isHidden ?? false; 
                  const txNeedsReview = isNeedsReviewTransaction(tx);
                  const reviewMessage = txNeedsReview ? getDataQualityReviewMessage(tx.data_quality_reason) : '';

                  return (
                    <tr 
                       key={tx.id || tx.date + tx.type + Math.random()} 
                       className={`hover:bg-gray-50 ${txIsHidden ? 'opacity-50 text-gray-400' : ''}`}
                    >{/* Ensure no whitespace before first td */} 
                      <td className="px-4 py-2 whitespace-nowrap text-sm font-medium">
                        <span className={`inline-flex items-center gap-2 ${txIsHidden ? '' : 'text-gray-800'}`}>
                          {txNeedsReview && (
                            <img
                              src="/icon-info-lrg.png"
                              alt="Needs review"
                              title={reviewMessage}
                              className="h-4 w-4 flex-shrink-0"
                              style={{ filter: 'brightness(0) saturate(100%) invert(52%) sepia(88%) saturate(504%) hue-rotate(359deg) brightness(91%) contrast(88%)' }}
                            />
                          )}
                          <span>{tx.type}</span>
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm"><span className={`${txIsHidden ? '' : 'text-gray-600'}`}>{details || '-'}</span></td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm"><span className={`${txIsHidden ? '' : 'text-gray-600'}`}>{formatDate(tx.date)}</span></td>
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-right font-medium ${txIsHidden ? '' : (displayAmount >= 0 ? 'text-green-600' : 'text-red-600')}`}>{formatCurrency(displayAmount)}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right"><span className={`${txIsHidden ? '' : 'text-gray-500'}`}>{formatCurrency(Math.abs(tx.commission ?? 0))}</span></td>
                      <td className="px-4 py-2 text-sm" title={notes}>
                         <span className={`line-clamp-2 ${txIsHidden ? '' : 'text-gray-600'}`}>{notes || '-'}</span>
                      </td>
                      <td className="sticky right-0 px-4 py-2 whitespace-nowrap text-right text-sm space-x-2 bg-white">
                         {/* Hide/Unhide Button */} 
                         <button 
                            onClick={() => handleHideTransactionClick(tx)} // Use new handler
                            className={`p-1 rounded ${txIsHidden ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-600' : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600'}`}
                            title={txIsHidden ? 'Unhide Transaction' : 'Hide Transaction'}
                            disabled={!tx.id} 
                         >
                              <img 
                                src={txIsHidden ? '/icon-unhide-lrg.png' : '/icon-hide-lrg.png'}
                                alt={txIsHidden ? 'Unhide Transaction' : 'Hide Transaction'}
                                className="h-4 w-4"
                              />
                         </button>
                         {/* Edit Button - Pass transaction data */} 
                         <button 
                            onClick={() => onEditTransactionClick(tx)}
                             className="p-1 rounded text-blue-500 hover:bg-blue-100 hover:text-blue-700"
                             title="Edit Transaction"
                             disabled={!tx.id} // Don't disable if hidden
                         >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                             <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                           </svg>
                         </button>
                         {/* Delete Button */} 
                         <button 
                             onClick={() => handleDeleteTransactionClick(tx.id!)}
                             className="p-1 rounded text-red-500 hover:bg-red-100 hover:text-red-700"
                             title="Delete Transaction"
                             disabled={!tx.id} // Don't disable if hidden
                         >
                           <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                               <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                         </button>
                      </td>
                    </tr>
                  );
                })
              ) : sortedTransactions.length > 0 && hasActiveTransactionFilters ? (
                <tr>{/* Ensure no whitespace before td */}
                  <td colSpan={7} className="px-4 py-4 text-center text-sm text-gray-500">No transactions match the current filters.</td>
                </tr>
              ) : (
                <tr>{/* Ensure no whitespace before td */} 
                  <td colSpan={7} className="px-4 py-4 text-center text-sm text-gray-500">No transactions found for this account.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div> 
      {/* --- End Transactions Section --- */} 
      {/* CSV Upload Modals */}
      {supportsCsvUpload && (
        <BrokerCsvUploadModal
          isOpen={isCsvOpen}
          accountId={account.id}
          broker="etrade"
          onClose={() => {
            setIsCsvOpen(false);
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem(`vester:portfolio:csvModal:etrade:${account.id}`);
            }
          }}
          onRefreshImpacted2={handleRefreshImpacted2}
          autoCloseOnSuccess
          onSuccess={(hints, warnings) => {
            const newTickers = hints?.newTickers;
            setCsvSuccessMessage(
              newTickers?.length
                ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
                : 'CSV uploaded successfully!'
            );
            if (warnings?.message) setCsvWarningMessage(warnings.message);
          }}
        />
      )}
      {supportsSchwabCsvUpload && (
        <BrokerCsvUploadModal
          isOpen={isSchwabCsvOpen}
          accountId={account.id}
          broker="schwab"
          onClose={() => {
            setIsSchwabCsvOpen(false);
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem(`vester:portfolio:csvModal:schwab:${account.id}`);
            }
          }}
          onRefreshImpacted2={handleRefreshImpacted2}
          autoCloseOnSuccess
          onSuccess={(hints, warnings) => {
            const newTickers = hints?.newTickers;
            setCsvSuccessMessage(
              newTickers?.length
                ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
                : 'CSV uploaded successfully!'
            );
            if (warnings?.message) setCsvWarningMessage(warnings.message);
          }}
        />
      )}
      {supportsFidelityCsvUpload && (
        <BrokerCsvUploadModal
          isOpen={isFidelityCsvOpen}
          accountId={account.id}
          broker="fidelity"
          onClose={() => {
            setIsFidelityCsvOpen(false);
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem(`vester:portfolio:csvModal:fidelity:${account.id}`);
            }
          }}
          onRefreshImpacted2={handleRefreshImpacted2}
          autoCloseOnSuccess
          onSuccess={(hints, warnings) => {
            const newTickers = hints?.newTickers;
            setCsvSuccessMessage(
              newTickers?.length
                ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
                : 'CSV uploaded successfully!'
            );
            if (warnings?.message) setCsvWarningMessage(warnings.message);
          }}
        />
      )}
      {supportsCoinbaseCsvUpload && (
        <BrokerCsvUploadModal
          isOpen={isCoinbaseCsvOpen}
          accountId={account.id}
          broker="coinbase"
          onClose={() => {
            setIsCoinbaseCsvOpen(false);
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem(`vester:portfolio:csvModal:coinbase:${account.id}`);
            }
          }}
          onRefreshImpacted2={handleRefreshImpacted2}
          autoCloseOnSuccess
          onSuccess={(hints, warnings) => {
            const newTickers = hints?.newTickers;
            setCsvSuccessMessage(
              newTickers?.length
                ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
                : 'CSV uploaded successfully!'
            );
            if (warnings?.message) setCsvWarningMessage(warnings.message);
          }}
        />
      )}
      {supportsVanguardCsvUpload && (
        <BrokerCsvUploadModal
          isOpen={isVanguardCsvOpen}
          accountId={account.id}
          broker="vanguard"
          onClose={() => {
            setIsVanguardCsvOpen(false);
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem(`vester:portfolio:csvModal:vanguard:${account.id}`);
            }
          }}
          onRefreshImpacted2={handleRefreshImpacted2}
          autoCloseOnSuccess
          onSuccess={(hints, warnings) => {
            const newTickers = hints?.newTickers;
            setCsvSuccessMessage(
              newTickers?.length
                ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
                : 'CSV uploaded successfully!'
            );
            if (warnings?.message) setCsvWarningMessage(warnings.message);
          }}
        />
      )}
      <DefaultCsvUploadModal
        isOpen={isDefaultCsvOpen}
        accountId={account.id}
        onClose={() => {
          setIsDefaultCsvOpen(false);
          // Clear persisted state on close
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem(`vester:portfolio:csvModal:default:${account.id}`);
          }
        }}
        onRefreshImpacted2={handleRefreshImpacted2}
        autoCloseOnSuccess
        onSuccess={(hints, warnings) => {
          const newTickers = hints?.newTickers;
          setCsvSuccessMessage(
            newTickers?.length
              ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
              : 'CSV uploaded successfully!'
          );
          if (warnings?.message) setCsvWarningMessage(warnings.message);
        }}
      />

      {/* Hide Account Confirmation Modal */}
      <ConfirmationModal
        isOpen={isAccountHideConfirmationOpen}
        onClose={handleCancelHideAccount}
        title="Hide Account"
        message={<>Are you sure you want to hide this entire account from all your friends?<br />(Note: Only Gold & Silver tier friends are affected)</>}
        confirmLabel="Yes, hide account"
        cancelLabel="Cancel"
        onConfirm={handleConfirmHideAccount}
        onCancel={handleCancelHideAccount}
        isLoading={isHidingAccount}
      />

      {/* Unhide Account Confirmation Modal */}
      <ConfirmationModal
        isOpen={isAccountUnhideConfirmationOpen}
        onClose={handleCancelUnhideAccount}
        title="Unhide Account"
        message={<>Are you sure you want to unhide this entire account from all your friends?<br />(Note: Only Gold & Silver tier friends are affected)</>}
        confirmLabel="Yes, unhide account"
        cancelLabel="Cancel"
        onConfirm={handleConfirmUnhideAccount}
        onCancel={handleCancelUnhideAccount}
        isLoading={isHidingAccount}
      />

      <ConfirmationModal
        isOpen={isUnlinkConfirmationOpen}
        onClose={handleCancelUnlinkAccount}
        title="Unlink External Account"
        message="Are you sure? This will stop automatic updates for this account."
        confirmLabel="Yes, unlink account"
        cancelLabel="Cancel"
        onConfirm={handleConfirmUnlinkAccount}
        onCancel={handleCancelUnlinkAccount}
        isLoading={isUnlinkingAccount}
        variant="danger"
      />

      {/* Hide Transaction Confirmation Modal */}
      <ConfirmationModal
        isOpen={isHideConfirmationOpen}
        onClose={handleCancelHideTransaction}
        title="Hide Transaction"
        message={transactionToHide ? getConfirmationMessage(transactionToHide, 'hide') : ''}
        confirmLabel={transactionToHide?.ticker ? `Yes, hide ${transactionToHide.ticker}` : 'Yes, hide transaction'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmTransaction}
        onCancel={handleCancelHideTransaction}
        isLoading={isHidingTransaction}
      />

      {/* Unhide Transaction Confirmation Modal */}
      <ConfirmationModal
        isOpen={isUnhideConfirmationOpen}
        onClose={handleCancelUnhideTransaction}
        title="Unhide Transaction"
        message={transactionToHide ? getConfirmationMessage(transactionToHide, 'unhide') : ''}
        confirmLabel={transactionToHide?.ticker ? `Yes, unhide ${transactionToHide.ticker}` : 'Yes, unhide transaction'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmTransaction}
        onCancel={handleCancelUnhideTransaction}
        isLoading={isHidingTransaction}
      />

      {/* Delete Transaction Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteTransactionConfirmationOpen}
        onClose={handleCancelDeleteTransaction}
        title="Delete Transaction"
        message="Are you sure you want to permanently delete this transaction? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeleteTransaction}
        onCancel={handleCancelDeleteTransaction}
        isLoading={isDeletingTransaction}
      />
    </div>
  );
};

export default EditAccountTransactions;
